import React from 'react';
import '@atlaskit/css-reset';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ExperienceSuccessTracker as ViewExperienceSuccessTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/result-declared/index.tsx';
import { ViewExperienceTrackingProvider } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { useIsEmbedView } from '@atlassian/jira-software-backlog/src/common/hooks/use-is-embed-view/index.tsx';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async.tsx';
import { checkAuthentication } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import { RedirectToLoginPage } from '@atlassian/jira-spa-apps-common/src/redirect/login/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import getMeta from '@atlassian/jira-get-meta';
import {
	BACKLOG_ROOT_SPA_APP_START_MARK,
	PACKAGE_NAME,
	TEAM_NAME,
} from '../../common/constants.tsx';
import { useIsCMPBacklogRoute, useOnce } from '../../common/utils.tsx';
import BacklogMetaAPIConsumerWrapper from './api-consumer/index.tsx';

// eslint-disable-next-line jira/ff/inline-usage
export const Backlog = () => {
	useOnce(() => {
		setMark(BACKLOG_ROOT_SPA_APP_START_MARK);
	});

	const [boardIdStr] = usePathParam('boardId');
	const boardId = Number(boardIdStr);

	const isCompanyManaged = useIsCMPBacklogRoute();
	const tenantContext = useTenantContext();
	const isAuthenticated = checkAuthentication(tenantContext);
	const hasSoftwareLicense = checkLicense(tenantContext, 'software');
	const isEmbedView = useIsEmbedView();

	if (!isAuthenticated) {
		return <RedirectToLoginPage />;
	}
	return (
		<ViewExperienceTrackingProvider
			experienceId={`${boardId}`}
			experience="viewBacklog"
			analyticsSource="backlog"
			edition={tenantContext.appEditions.software}
			application={JIRA_SOFTWARE}
			additionalAttributes={{
				isClassic: isCompanyManaged,
				isUIF: true,
				jswSprintPlanningWhiteboards: fg('sprint_planning_whiteboards_kill_switch'),
				shard: getMeta('ajs-shard') ?? {},
				region: getMeta('ajs-region') ?? {},
				...(typeof isEmbedView === 'boolean' ? { isEmbed: isEmbedView } : {}),
			}}
		>
			{hasSoftwareLicense ? (
				<BacklogMetaAPIConsumerWrapper isCompanyManaged={isCompanyManaged} />
			) : (
				<ViewExperienceSuccessTracker location="ng-backlog-context-query">
					<AsyncNoSoftwareLicense />
				</ViewExperienceSuccessTracker>
			)}
		</ViewExperienceTrackingProvider>
	);
};

const BacklogWrapper = () => (
	<UFOSegment name="backlog-wrapper">
		<MarkProductStart />
		<JSErrorBoundary
			id="spa-app-bootstrap.next-gen.backlog"
			fallback="flag"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
		>
			<Backlog />
		</JSErrorBoundary>
	</UFOSegment>
);

export default BacklogWrapper;
